// src/pages/ProductDetail.jsx
import React, { useState, useEffect } from "react";
import { ShoppingCart, Minus, Plus } from "lucide-react";
import RatingStars from "../components/RatingStars";
import { PRIMARY_COLOR, ACCENT_BG } from "../App";
import products from "../data/products.json";

const ProductDetail = ({ slug, onAddToCart }) => {
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const MAX_PER_PRODUCT = 10;

  useEffect(() => {
    if (!slug) return;
    const found = products.find((p) => p.slug === slug);
    setProduct(found || null);
  }, [slug]);

  if (!product)
    return (
      <div className="min-h-screen pt-32 bg-gray-900 text-center p-8 text-red-500">
        Product not found.
      </div>
    );

  // ✅ FIX: full category fallback support
  const categoryName =
    product?.category_name ||
    product?.category ||
    product?.category_slug ||
    product?.["category-slug"] ||
    "N/A";

  const price = parseFloat(product.price || 0).toFixed(2);
  const stock = product.stock || 0;
  const maxSelectableQty = Math.max(1, Math.min(stock, MAX_PER_PRODUCT));
  const imageUrl = product?.image
    ? product.image.startsWith("/")
      ? product.image
      : `/${product.image}`
    : "/products/default.jpg";
  const rating = parseFloat(product.rating || 0);

  return (
    <div className="min-h-screen pt-32 bg-gray-900 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto bg-gray-800 rounded-xl shadow-2xl p-6 md:p-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          
          {/* Product Image */}
          <div>
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-auto object-cover rounded-xl border-4 border-teal-700 shadow-xl"
            />
          </div>

          {/* Product Info */}
          <div className="flex flex-col justify-center">
            <p className={`text-sm font-bold uppercase tracking-widest ${PRIMARY_COLOR} mb-2`}>
              {categoryName}
            </p>

            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              {product.name}
            </h1>

            <div className="flex items-center space-x-4 mb-6">
              <RatingStars rating={rating} />
              <span className="text-gray-400">(0 reviews)</span>
              <span
                className={`text-sm font-semibold px-3 py-1 rounded-full ${
                  stock > 0 ? "bg-green-600" : "bg-red-600"
                } text-white`}
              >
                {stock > 0 ? `In Stock (${stock})` : "Out of Stock"}
              </span>
            </div>

            <p className="text-3xl font-bold text-white mb-6">£{price}</p>

            <p className="text-gray-300 mb-8 leading-relaxed">
              {product.description}
            </p>

            {stock > 0 ? (
              <div className="flex space-x-4 items-center">
                
                {/* Quantity Selector */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setQuantity((prev) => Math.max(prev - 1, 1))}
                    className="p-1 border border-gray-600 rounded-md text-gray-400 hover:bg-gray-700"
                  >
                    <Minus className="w-4 h-4" />
                  </button>

                  <input
                    type="number"
                    value={quantity}
                    readOnly
                    className="w-10 text-center bg-gray-700 text-white rounded-md text-sm"
                  />

                  <button
                    onClick={() => setQuantity((prev) => Math.min(prev + 1, maxSelectableQty))}
                    className="p-1 border border-gray-600 rounded-md text-gray-400 hover:bg-gray-700"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-xs text-gray-400">
                  Max {MAX_PER_PRODUCT} per product per order.
                </p>

                {/* Add to Cart */}
                <button
                  onClick={() => onAddToCart(product, Math.min(quantity, maxSelectableQty))}
                  className={`flex-1 flex items-center justify-center p-3 rounded-lg text-lg font-semibold transition-transform duration-200 transform hover:scale-[1.01] shadow-xl ${ACCENT_BG} text-white`}
                >
                  <ShoppingCart className="w-5 h-5 mr-2" /> Add to Cart
                </button>
              </div>
            ) : (
              <button
                disabled
                className="bg-red-800 text-white p-3 rounded-lg text-lg font-semibold cursor-not-allowed"
              >
                Sold Out
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
